import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Select } from '../ui/newSelect';
import { X, Loader2, CircleHelp, FileDown, Trash2, CloudUpload, Sparkles } from 'lucide-react';
import get from 'lodash/get';
import { Actions as USAGEACTIONS } from '../../store/actions/usageActions';

const usageImportOptions = [
  { label: 'Business Travel: Air', value: 'business_air_travel' },
  { label: 'Business Travel: Rail', value: 'business_rail_travel' },
  { label: 'Business Travel: Road', value: 'business_road_travel' },
  { label: 'Construction', value: 'construction' },
  { label: 'Electricity', value: 'electricity_with_fuel_mix' },
  { label: 'Employee Commuting: Rail', value: 'commuting_rail_travel' },
  { label: 'Employee Commuting: Road', value: 'commuting_road_travel' },
  { label: 'Food and Drinks', value: 'food_drink' },
  { label: 'Freight With Distance', value: 'freight_distances_included' },
  { label: 'Freight Without Distance', value: 'freight_with_no_distances_use_api', googleApiKey: true },
  { label: 'Gas', value: 'gas' },
  { label: 'Paper', value: 'paper' },
];

const UploadUsageData = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const usageUploadType = useSelector((state) => state.usageUploadType);
  const usageUploadModal = useSelector((state) => state.usageUploadModal);
  const importUploadLink = useSelector((state) => state.importUploadLink);
  const hasGoogleApiKey = useSelector((state) => !!get(state, 'organisation.details.googleApiKey'));
  const branding = useSelector((state) => state.branding);
  const importModal = useSelector((state) => state.importModal);
  const showLoader = useSelector((state) => state.showLoader);
  const progressState = useSelector((state) => (state.progress >= 0 ? state.progress : null));
  const alertObj = useSelector((state) => state.alertMessage || {});
  const supportEmail = useSelector((state) => get(state, 'whiteLabelAdmin.supportEmail'));

  const [file, setFile] = useState(null);
  const [errorMessage, setErrorMessage] = useState(false);
  const [inputDisabled, setInputDisabled] = useState(false);
  const [importMessage, setImportMessage] = useState(false);

  useEffect(() => {
    dispatch(USAGEACTIONS.getTemplates());
  }, [dispatch]);

  useEffect(() => {
    const { severity, message } = alertObj;
    if (!usageUploadModal) return;
    if (!showLoader) setImportMessage(false);
    if (severity === 'error') setErrorMessage(message);
    if (severity === 'success') setImportMessage(message);
    if (importModal) {
      setImportMessage(false);
      setErrorMessage(false);
    }
  }, [showLoader, alertObj, usageUploadModal, importModal]);

  const tooltipContent = (
    <div className='space-y-2'>
      <p>
        To upload 'freight without distance' template, you need to set up a Google account to enable DistanceMatrix API. An
        API key will be provided for importing freight. Google will bill you directly for API keys.
      </p>
      <p>
        For help setting up an account and obtaining a key, contact us at{' '}
        <a className='underline text-primary' href={`mailto:${supportEmail}`}>
          {supportEmail}
        </a>
        .
      </p>
      <p>Once you have the API key, add it to your organisation details in the 'Organisation' page.</p>
    </div>
  );

  const downloadFile = usageUploadType?.value || usageImportOptions[0].value;

  const goToIntelligentImport = () => {
    setErrorMessage(false);
    setImportMessage(false);
    dispatch(USAGEACTIONS.showUploadUsageDataModal(false));
    setFile(null);
    history.push('/intelligent-import');
  };

  if (!usageUploadModal) return null;

  return (
    <Dialog open={!!usageUploadModal} onOpenChange={() => dispatch(USAGEACTIONS.showUploadUsageDataModal(false))}>
      <DialogContent className='max-w-lg p-0 bg-background' style={{ overflow: 'visible' }}>
        <Card className='bg-card text-card-foreground border-none shadow-none'>
          <DialogHeader className='bg-header text-header-foreground px-6 pt-6 pb-2'>
            <DialogTitle className='font-default flex items-center justify-between'>
              Import usage data
              <Button
                variant='ghost'
                size='icon'
                className='ml-auto text-muted-foreground hover:text-foreground'
                onClick={() => {
                  setErrorMessage(false);
                  setImportMessage(false);
                  dispatch(USAGEACTIONS.showUploadUsageDataModal(false));
                  setFile(null);
                }}>
                <X size={16} strokeWidth={1.5} />
              </Button>
            </DialogTitle>
          </DialogHeader>
          <CardContent className='px-6 pb-6'>
            {importMessage && <div className='text-center text-lg font-bold mb-4'>{importMessage}</div>}
            {showLoader && !errorMessage && (
              <div className='flex flex-col items-center justify-center py-8'>
                <Loader2 size={24} strokeWidth={1.5} className='animate-spin text-primary mb-2' />
                <div className='font-semibold text-lg'>Loading...</div>
              </div>
            )}
            {errorMessage && <div className='text-center text-destructive text-lg font-bold mb-4'>{errorMessage}</div>}
            {!progressState && !showLoader && !errorMessage && (
              <>
                <div className='mb-4'>
                  <label className='block text-sm font-semibold mb-1'>
                    Usage type <em className='text-destructive'>*</em>
                  </label>
                  <div className='flex items-center gap-2'>
                    <Select
                      id='ImportUsageData-usageType'
                      options={usageImportOptions}
                      value={usageUploadType?.value || null}
                      onChange={(value) => {
                        const selectedOption = usageImportOptions.find((option) => option.value === value) || null;
                        dispatch(USAGEACTIONS.uploadUsageType(selectedOption));
                        setFile(null);
                      }}
                      placeholder='Select usage type'
                      size='sm'
                    />
                    {usageUploadType?.googleApiKey && !hasGoogleApiKey && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button variant='ghost' size='icon' className='text-primary'>
                              <CircleHelp size={18} strokeWidth={1.5} />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent className='max-w-xs'>{tooltipContent}</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                  {usageUploadType && (
                    <div className='mt-2'>
                      <div className='mb-2'>
                        <span className='font-semibold'>Download template:</span>
                        <a
                          href={`https://s3.eu-west-2.amazonaws.com/downloads.compareyourfootprint.com/${downloadFile}_template.xlsx`}
                          className='ml-2 text-primary underline hover:text-primary-foreground'
                          target='_blank'
                          rel='noopener noreferrer'>
                          Excel <FileDown size={16} strokeWidth={1.5} className='ml-1 inline' />
                        </a>
                      </div>
                      {file && (
                        <div className='mb-2'>
                          <span className='font-semibold'>File name:</span> <span>{file?.name}</span>
                        </div>
                      )}
                      {usageUploadType?.googleApiKey && !hasGoogleApiKey && (
                        <div className='text-destructive text-sm font-semibold mt-2'>
                          Import requires an API key. Please hover over tooltip for guidance.
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className='flex flex-wrap gap-4 justify-center'>
                  {file?.name ? (
                    <Button
                      className='bg-destructive text-destructive-foreground'
                      onClick={() => {
                        setInputDisabled(true);
                        setFile(null);
                        setTimeout(() => setInputDisabled(false), 10);
                      }}>
                      <Trash2 size={16} strokeWidth={1.5} className='mr-2' /> Remove File
                    </Button>
                  ) : (
                    <label htmlFor='import_input' style={{ marginBottom: 0 }}>
                      <Button
                        asChild
                        disabled={!usageUploadType || (!hasGoogleApiKey && usageUploadType?.googleApiKey)}>
                        <span><CloudUpload size={16} strokeWidth={1.5} className='mr-2 inline' /> Upload File</span>
                      </Button>
                      <input
                        id='import_input'
                        type='file'
                        disabled={file?.name || inputDisabled}
                        accept='.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel'
                        className='hidden'
                        onChange={(e) => {
                          const newFile = e.target.files && e.target.files[0];
                          setFile(newFile);
                          dispatch(USAGEACTIONS.getUploadLink(newFile));
                          e.target.value = '';
                        }}
                      />
                    </label>
                  )}
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>
                          <Button
                            disabled={
                              !file || !importUploadLink || (!hasGoogleApiKey && usageUploadType?.googleApiKey) || showLoader
                            }
                            onClick={() => {
                              if (
                                !file ||
                                !importUploadLink ||
                                (!hasGoogleApiKey && usageUploadType?.googleApiKey) ||
                                showLoader
                              )
                                return;
                              dispatch(USAGEACTIONS.processUpload(file));
                              setFile(null);
                            }}>
                            {showLoader && <Loader2 size={16} strokeWidth={1.5} className='animate-spin mr-2' />}
                            {showLoader ? 'Import in progress...' : 'Start Import'}
                          </Button>
                        </span>
                      </TooltipTrigger>
                      {(!file || !importUploadLink || (!hasGoogleApiKey && usageUploadType?.googleApiKey)) &&
                        !showLoader && (
                          <TooltipContent className='max-w-xs'>
                            {(!file && 'Please upload a file to import.') ||
                              (!importUploadLink && 'Upload link not ready.') ||
                              (!hasGoogleApiKey && usageUploadType?.googleApiKey && 'API key required for this import.')}
                          </TooltipContent>
                        )}
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <div className='my-6'>
                  <Separator />
                </div>
                <div className='flex justify-center'>
                  <Button variant='outline' onClick={goToIntelligentImport}>
                    <Sparkles size={16} strokeWidth={1.5} className='mr-2' />
                    Try the AI-powered import
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </DialogContent>
    </Dialog>
  );
};

export default UploadUsageData;
